import { startCase } from 'lodash';
import {
	IFGAGroupMapping,
	IFGAMappableSchemaResponse,
	IMappableResource,
	IRelationOption
} from 'types/api';
import { IOption } from 'types/fields';
import { sortOptions } from './form';

const CUSTOM_ROLE_FGA_TYPE = 'customrole';

// A scoped custom role is stored under this relation. It is fixed by the authz schema, so the client
// holds it as a constant rather than the server sending it on every mappable payload.
const CUSTOM_ROLE_RELATION = 'hasScope';

// The role and scope a composed custom role resource is shown as.
interface IScopedCustomRole {
	resource: string;
	roleId: string;
	roleName: string;
	scopeType: string;
	scopeId: string;
}

// Mirrors backend BuildScopedCustomRoleID: CR_<scope>_<slug>, where a tag scope id already carries
// its companyID prefix. This is a stored identifier format, so it is effectively frozen.
const scopedCustomRoleResource = (scopeId: string, slug: string): string =>
	`CR_${scopeId}_${slug}`;

export interface IFGAMappingSection {
	fgaType: string;
	relationOptions: IOption[];
	resourceOptions: IOption[];
	mappings: IFGAGroupMapping[];
}

export interface IFGAMappingModel {
	hasSchema: boolean;
	fgaTypes: string[];
	relationOptions: Record<string, IOption[]>;
	resourceOptions: Record<string, IOption[]>;
	/** roleId -> scopeId -> the scoped custom role resource the pair is stored as. */
	storedResourceByRoleAndScope: Record<string, Record<string, string>>;
	/** The scoped custom role resource -> the role and scope it is shown as. */
	roleAndScopeByStoredResource: Record<
		string,
		{ roleId: string; scopeType: string; scopeId: string }
	>;
	hasCustomRoles: boolean;
}

const toOptions = (resources: IMappableResource[]): IOption[] =>
	resources.map((resource) => ({
		value: resource.resource,
		label: resource.displayName
	}));

const relationOptionsForType = (
	relations: string[],
	displayNames: Record<string, string>,
	roleOptions: IOption[]
): IOption[] => {
	const schemaOptions = relations.map((relation) => ({
		value: relation,
		label: displayNames[relation] ?? startCase(relation)
	}));
	// A custom role is free to carry the same name as a relation of its type. Two identical entries
	// granting different things is a bad way to lose an argument about access, so say which one is
	// the role.
	const taken = new Set(schemaOptions.map((o) => o.label.toLowerCase()));
	return schemaOptions
		.concat(
			roleOptions.map((option) =>
				taken.has(option.label.toLowerCase())
					? { ...option, label: `${option.label} (custom role)` }
					: option
			)
		)
		.sort(sortOptions);
};

export const parseMappableFGASchema = (
	res: IFGAMappableSchemaResponse
): IFGAMappingModel => {
	const resourcesByType: Record<string, IMappableResource[]> = {};
	const relationsByType: Record<string, IRelationOption[]> = {};
	(res.mappableResources ?? []).forEach(({ type, relations, resources }) => {
		resourcesByType[type] = resources ?? [];
		relationsByType[type] = relations ?? [];
	});

	const displayNamesByType: Record<string, Record<string, string>> = {};
	Object.entries(relationsByType).forEach(([type, relations]) => {
		relations.forEach(({ relation, displayName }) => {
			if (!displayName) {
				return;
			}
			if (!displayNamesByType[type]) {
				displayNamesByType[type] = {};
			}
			displayNamesByType[type][relation] = displayName;
		});
	});

	// The scope types a custom role may be scoped to come from the server; they are absent for a
	// customer project, so a role is offered only when the server advertised them, never by guesswork.
	const { customRoleScopeTypes } = res;
	const canComposeCustomRoles = !!customRoleScopeTypes?.length;

	// The scoped custom role resources are composed here from each definition and the loaded scopes
	// of its type, rather than enumerated by the server, restricted to the scope types the server
	// allows (project, tag), so a role is never offered against a type with no scoped tuple.
	const customRoles: IScopedCustomRole[] = [];
	(canComposeCustomRoles ? (res.customRoleDefinitions ?? []) : []).forEach(
		(def) => {
			customRoleScopeTypes!.forEach((scopeType) => {
				(resourcesByType[scopeType] ?? []).forEach((scope) => {
					customRoles.push({
						resource: scopedCustomRoleResource(scope.resource, def.slug),
						roleId: def.roleId,
						roleName: def.roleName,
						scopeType,
						scopeId: scope.resource
					});
				});
			});
		}
	);
	const storedResourceByRoleAndScope: IFGAMappingModel['storedResourceByRoleAndScope'] =
		{};
	const roleAndScopeByStoredResource: IFGAMappingModel['roleAndScopeByStoredResource'] =
		{};
	const roleOptionsByType: Record<string, IOption[]> = {};

	customRoles.forEach((role) => {
		if (!storedResourceByRoleAndScope[role.roleId]) {
			storedResourceByRoleAndScope[role.roleId] = {};
		}
		storedResourceByRoleAndScope[role.roleId][role.scopeId] = role.resource;
		roleAndScopeByStoredResource[role.resource] = {
			roleId: role.roleId,
			scopeType: role.scopeType,
			scopeId: role.scopeId
		};

		const options = roleOptionsByType[role.scopeType] ?? [];
		if (!options.some((option) => option.value === role.roleId)) {
			options.push({ value: role.roleId, label: role.roleName });
		}
		roleOptionsByType[role.scopeType] = options;
	});

	const fgaTypes = Object.keys(resourcesByType)
		.filter((fgaType) => resourcesByType[fgaType].length > 0)
		.sort((a, b) => a.localeCompare(b));

	const relationOptions: Record<string, IOption[]> = {};
	const resourceOptions: Record<string, IOption[]> = {};

	fgaTypes.forEach((fgaType) => {
		relationOptions[fgaType] = relationOptionsForType(
			(relationsByType[fgaType] ?? []).map((r) => r.relation),
			displayNamesByType[fgaType] ?? {},
			roleOptionsByType[fgaType] ?? []
		);
		resourceOptions[fgaType] = toOptions(resourcesByType[fgaType]);
	});

	return {
		hasSchema: !!res.fgaAvailable,
		fgaTypes,
		relationOptions,
		resourceOptions,
		storedResourceByRoleAndScope,
		roleAndScopeByStoredResource,
		hasCustomRoles: customRoles.length > 0
	};
};

// A custom role mapping is stored as hasScope on the scoped role resource, but is shown as the role
// against its scope, so both directions are translated here.
export const toDisplayMapping = (
	model: IFGAMappingModel,
	mapping: IFGAGroupMapping
): IFGAGroupMapping => {
	const role =
		mapping.fgaType === CUSTOM_ROLE_FGA_TYPE &&
		mapping.relation === CUSTOM_ROLE_RELATION
			? model.roleAndScopeByStoredResource[mapping.resource]
			: undefined;
	if (!role) {
		return mapping;
	}
	return {
		...mapping,
		fgaType: role.scopeType,
		relation: role.roleId,
		resource: role.scopeId
	};
};

/** Returns null for a custom role paired with a scope it has no entry for. */
export const toStoredMapping = (
	model: IFGAMappingModel,
	mapping: IFGAGroupMapping
): IFGAGroupMapping | null => {
	const scopes = model.storedResourceByRoleAndScope[mapping.relation];
	if (!scopes) {
		return mapping;
	}
	const resource = scopes[mapping.resource];
	if (!resource) {
		return null;
	}
	return {
		...mapping,
		fgaType: CUSTOM_ROLE_FGA_TYPE,
		relation: CUSTOM_ROLE_RELATION,
		resource
	};
};

export const getMappingSections = (
	model: IFGAMappingModel,
	storedMappings: IFGAGroupMapping[]
): IFGAMappingSection[] => {
	const mappingsByType: Record<string, IFGAGroupMapping[]> = {};
	storedMappings
		.map((mapping) => toDisplayMapping(model, mapping))
		.forEach((mapping) => {
			if (!mappingsByType[mapping.fgaType]) {
				mappingsByType[mapping.fgaType] = [];
			}
			mappingsByType[mapping.fgaType].push(mapping);
		});

	// A stored mapping whose role is gone, deleted or dropped from a truncated table, stays under
	// the customrole type, so keep that section for it.
	const keepCustomRoleType =
		!model.hasCustomRoles || !!mappingsByType[CUSTOM_ROLE_FGA_TYPE];

	// A type the response carries no resources for still needs a section when a stored mapping
	// lands in it, or the mapping renders nowhere: invisible, undeletable, re-saved on every
	// submit, and rejected by the server on every later save.
	const sectionTypes = model.fgaTypes
		.concat(
			Object.keys(mappingsByType).filter(
				(fgaType) => !model.fgaTypes.includes(fgaType)
			)
		)
		.sort((a, b) => a.localeCompare(b));

	return sectionTypes
		.filter((fgaType) => fgaType !== CUSTOM_ROLE_FGA_TYPE || keepCustomRoleType)
		.map((fgaType) => ({
			fgaType,
			relationOptions: model.relationOptions[fgaType] ?? [],
			resourceOptions: model.resourceOptions[fgaType] ?? [],
			mappings: mappingsByType[fgaType] ?? []
		}));
};

export const applySectionMappings = (
	model: IFGAMappingModel,
	storedMappings: IFGAGroupMapping[],
	fgaType: string,
	sectionMappings: IFGAGroupMapping[]
): IFGAGroupMapping[] => {
	const submitted = sectionMappings
		.filter(
			(mapping) =>
				!!mapping.group &&
				!!mapping.relation &&
				!!mapping.resource &&
				!!mapping.fgaType
		)
		.map((mapping) => toStoredMapping(model, mapping))
		.filter((mapping): mapping is IFGAGroupMapping => !!mapping);

	// The rows being replaced are matched on the section they are shown in, not on how they store.
	const untouched = storedMappings.filter(
		(mapping) => toDisplayMapping(model, mapping).fgaType !== fgaType
	);

	return [...untouched, ...submitted];
};
